import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone, Clock } from "lucide-react";
import { activateThirdParty } from "@/lib/third-party";

const QuoteFormContext = createContext<{ openQuoteForm: () => void }>({
  openQuoteForm: () => {},
});

export const useQuoteForm = () => useContext(QuoteFormContext);

export const QuoteFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openQuoteForm = useCallback(() => {
    activateThirdParty();
    setOpen(true);
  }, []);
  const closeQuoteForm = useCallback(() => setOpen(false), []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <QuoteFormContext.Provider value={{ openQuoteForm }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closeQuoteForm(); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative w-full max-w-lg max-h-[calc(100dvh-32px)] overflow-y-auto rounded-2xl shadow-2xl bg-card p-6 md:p-7"
            >
              <button
                onClick={closeQuoteForm}
                className="absolute top-3 right-3 z-10 p-2 rounded-full bg-muted hover:bg-muted/80 text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>

              <h3 className="font-heading text-2xl text-foreground mb-1 font-bold">Request a Free Estimate</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Fill out the form and Villalobos Prime Services will get back to you quickly with clear, honest guidance for your project.
              </p>

              <iframe
                src="https://link.systemic-digital.net/widget/form/HY0j326zxDKuZH6dz70Z"
                style={{ width: "100%", border: "none", borderRadius: "10px", minHeight: "600px" }}
                id="inline-modal-HY0j326zxDKuZH6dz70Z"
                data-layout="{'id':'INLINE'}"
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Website Form"
                data-height="746"
                data-layout-iframe-id="inline-modal-HY0j326zxDKuZH6dz70Z"
                data-form-id="HY0j326zxDKuZH6dz70Z"
                title="Website Form"
              />

              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-muted-foreground text-sm pt-3 border-t border-border/60 mt-3">
                <a href="tel:7863065704" className="flex items-center gap-1.5 font-semibold text-foreground hover:text-primary transition-colors">
                  <Phone className="h-4 w-4 text-primary" /> (786) 306-5704
                </a>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-primary" />
                  Mon–Fri 8 AM – 7 PM · Sat 8 AM – 4 PM
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </QuoteFormContext.Provider>
  );
};
